import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToggleCommentLikeMutation } from '@/redux/like/like.api.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import { UserState } from '@/redux/auth/auth.d';

/**
 * The one reaction a comment has. Optimistic like LikeButton: the click shows
 * immediately and reverts on failure, because the mutation deliberately does
 * not invalidate the thread.
 */
export default function CommentHeartButton({
  commentId,
  liked,
  count,
}: {
  commentId: string;
  liked: boolean;
  count: number;
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state: { auth: UserState }) => state.auth);
  const [toggleLike] = useToggleCommentLikeMutation();

  const [isLiked, setIsLiked] = useState(liked);
  const [likeCount, setLikeCount] = useState(count);
  useEffect(() => {
    setIsLiked(liked);
    setLikeCount(count);
  }, [liked, count]);

  const handleToggle = async () => {
    if (!user) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(wasLiked ? likeCount - 1 : likeCount + 1);

    try {
      await toggleLike(commentId).unwrap();
    } catch {
      setIsLiked(wasLiked);
      setLikeCount(likeCount);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-pressed={isLiked}
      aria-label={isLiked ? 'Remove your like' : 'Like this comment'}
      className="-ml-2 h-7 gap-1.5 px-2 text-xs text-muted-foreground"
      onClick={handleToggle}
    >
      <Heart
        className={cn('h-4 w-4', isLiked && 'fill-red-500 text-red-500')}
      />
      {likeCount > 0 && <span>{likeCount}</span>}
    </Button>
  );
}
